import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'

import { get, put, Params } from 'api/fetch'

import { Status, DistributionMethods, Pagination } from './types/types'

interface Review {
  id: string
  reviewer: string
  status: Status
  comment: string
  amount: number
  created_at: string
  updated_at: string
}

export interface PetitionMetadata {
  is_active?: boolean
  max_claims: number
  password: string
  valid_from: string
  valid_to: string
}

export interface Drop {
  id: number
  slug: string
  name: string
  email: string
  country: string
  city: string
  description: string
  year: number
  secret_code: number
  drop_url: string
  private_drop: boolean
  virtual_drop: boolean
  image_url: string
  animation_url: string | null
  start_date: string
  end_date: string
  expiry_date: string
  from_admin: boolean
  drop_template: string | null
  fancy_id: string
  created_date: string
  drop_template_id: string | null
}
export interface Petition {
  id: string
  reviews: Review[]
  status: Status
  issuer: string
  drop: Drop
  drop_id: number
  issuer_id: string
  distribution_method: DistributionMethods
  amount_requested: number
  created_at: string
  updated_at: string
  expires_date?: string
  amount_approved: number | null
  website_metadata?: PetitionMetadata
  secret_metadata?: PetitionMetadata
}

type Resp =
  | undefined
  | {
      results: Petition[]
      meta: Pagination
    }

const apiUrl = process.env.REACT_APP_API_V2 ?? ''

const getPetitions = async (params: Params): Promise<Resp> => {
  const res = await get(`${apiUrl}/drop-requests`, params)

  if (res.status === 200 && res.parsedBody) {
    return res.parsedBody as Resp
  }

  if (res.status === 204) {
    return undefined
  }

  throw new Error('There was an error retrieving the data')
}

export const useGetPetitionsQuery = (defaultPetitionsParams: Params) => {
  const [queryParams, setQueryParams] = useState(defaultPetitionsParams)

  const petitionsQuery = useQuery(['getPetitions', queryParams], () => getPetitions(queryParams), {
    keepPreviousData: true,
  })

  const currentDropId = queryParams.drop_id || null

  return useMemo(() => ({ setQueryParams, petitionsQuery, currentDropId }), [setQueryParams, petitionsQuery, currentDropId])
}

const editPetitions = async ({ id, ...params }: any): Promise<{ ok: boolean }> => {
  const res = await put(`${apiUrl}/drop-requests/${id}/review`, params)

  if (res.status === 204) {
    return res
  }

  throw new Error('There was an error retrieving the data')
}

export const usePutPetitionQuery = () => {
  const queryClient = useQueryClient()

  return useMutation((data) => editPetitions(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['getPetitions'])
    },
  })
}
